import {
  action,
  fileList,
  handleBeforeUpload,
  handleFileChanged,
  handleUpload } from
"@src/helpers/fileUpload";
import { getUuid } from "@src/helpers/functions";
import {
  AppDispatch,
  createAdditionalReportList,
  createAdditionalReportListClear,
  createAdditionalReportListRequest,
  createAdditionalReportRequest,
  AdditionalReportList,
  getReadingListRequest,
  getReadingRequest,
  useDynamicSelector } from
"@src/redux";
import {
  Button,
  Col,
  Form,
  FormInstance,
  Row,
  Spin,
  Tooltip,
  Upload } from
"antd";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

type Props = {
  onCancel: Function;
  form: FormInstance;
};

const AdditionalReports = (props: Props) => {
  const { onCancel, form } = props;
  const { t } = useTranslation();
  const ecg = Form.useWatch("ecg", form);
  const eyeTest = Form.useWatch("eye_test", form);
  const bloodTest = Form.useWatch("blood_test", form);
  const dispatch = useDispatch<AppDispatch>();
  const { reading } = useDynamicSelector("get_reading_details");
  const { id } = useParams();

  const {
    result: createAdditionalReportListResult,
    loading: createAdditionalReportListLoading
  } = useSelector(createAdditionalReportList);

  useEffect(() => {
    if (bloodTest || eyeTest || ecg) {
      form.setFieldsValue({
        bloodTest: bloodTest?.fileList || bloodTest || [],
        eyeTest: eyeTest?.fileList || eyeTest || [],
        ecg: ecg?.fileList || ecg || []
      });
    }
  }, [bloodTest, eyeTest, ecg]);

  const handleFinish = () => {
    let json = [
    {
      fileUrl: bloodTest?.[0]?.url,
      readingId: reading?.id,
      reportName: `blood_test-${getUuid()}`
    },
    {
      fileUrl: ecg?.[0]?.url,
      readingId: reading?.id,
      reportName: `ecg-${getUuid()}`
    },
    {
      fileUrl: eyeTest?.[0]?.url,
      readingId: reading?.id,
      reportName: `eyeTest-${getUuid()}`
    }];

    dispatch(
      createAdditionalReportListRequest({
        data: json.filter((list) => list.fileUrl)
      })
    );
  };

  useEffect(() => {
    if (createAdditionalReportListResult?.status === "success") {
      toast.success("Additional reports updated successfully.");
      dispatch(getReadingListRequest({ filter: { subjectId: id } }));
      onCancel();
      dispatch(createAdditionalReportListClear());
    }
  }, [createAdditionalReportListResult]);

  return (
    <Form form={form} onFinish={handleFinish}>
      <Form.Item
        label={t("ecg")}
        name={"ecg"}
        className="additional-reports-label-container">
        
        <Upload
          action={action}
          customRequest={(e) => handleUpload(e)}
          beforeUpload={(args) =>
          handleBeforeUpload(args, "document", [
          "image/jpeg",
          "image/jpg",
          "image/png"]
          )}

          onChange={(e) => handleFileChanged(e)}
          fileList={ecg?.fileList || ecg || []}
          maxCount={1}
          disabled={
          reading?.additionalReportList &&
          reading?.additionalReportList?.some((report) =>
          report?.reportName?.includes("ecg")
          )}>

          
          {reading?.additionalReportList &&
          reading?.additionalReportList?.some((report) =>
          report?.reportName?.includes("ecg")
          ) ?
          "" :

          <Tooltip title={"Allow only PNG, JPG and JPEG images"}>
              <Button type="primary">{t("upload")}</Button>
            </Tooltip>}
          {" "}
        </Upload>
      </Form.Item>
      <Form.Item
        label={t("eye_test")}
        name={"eye_test"}
        className="additional-reports-label-container">
        
        <Upload
          action={action}
          customRequest={(e) => handleUpload(e)}
          beforeUpload={(args) =>
          handleBeforeUpload(args, "document", [
          "image/jpeg",
          "image/jpg",
          "image/png"]
          )}

          onChange={(e) => handleFileChanged(e)}
          fileList={eyeTest?.fileList || eyeTest || []}
          maxCount={1}
          disabled={
          reading?.additionalReportList &&
          reading?.additionalReportList?.some((report) =>
          report?.reportName?.includes("eye_test")
          )}>

          
          {reading?.additionalReportList &&
          reading?.additionalReportList?.some((report) =>
          report?.reportName?.includes("eye_test")
          ) ?
          "" :

          <Tooltip title={"Allow only PNG, JPG and JPEG images"}>
              <Button type="primary">{t("upload")}</Button>
            </Tooltip>}
          {" "}
        </Upload>
      </Form.Item>
      <Form.Item
        label={t("blood_test")}
        name={"blood_test"}
        className="additional-reports-label-container">
        
        <Upload
          action={action}
          customRequest={(e) => handleUpload(e)}
          beforeUpload={(args) =>
          handleBeforeUpload(args, "document", [
          "image/jpeg",
          "image/jpg",
          "image/png"]
          )}

          onChange={(e) => handleFileChanged(e)}
          maxCount={1}
          fileList={bloodTest?.fileList || bloodTest || []}
          disabled={
          reading?.additionalReportList &&
          reading?.additionalReportList?.some((report) =>
          report?.reportName?.includes("blood_test")
          ) ?
          true :
          false}>

          
          {reading?.additionalReportList &&
          reading?.additionalReportList?.some((report) =>
          report?.reportName?.includes("blood_test")
          ) ?
          "" :

          <Tooltip title={"Allow only PNG, JPG and JPEG images"}>
              <Button type="primary">{t("upload")}</Button>
            </Tooltip>}
          
        </Upload>
      </Form.Item>
      <div className="additional-reports-footer-container">
        <Button danger onClick={() => onCancel()}>
          {t("close")}
        </Button>
        {reading?.additionalReportList &&
        reading?.additionalReportList?.some((report) =>
        ["blood_test", "ecg", "eye_test"].some((tag) =>
          report?.reportName?.includes(tag)
        )
        ) ?
        "" :

        <Button
          type="primary"
          htmlType="submit"
          loading={createAdditionalReportListLoading}>
          
            {t("submit")}
          </Button>}
        
      </div>
    </Form>);

};

export default AdditionalReports;